// src/pages/Home.jsx
import { Star, ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react"
import { useState, useEffect } from "react"
import { reviews } from "../data/reviews";

export default function Home() {
  // Image slideshow state
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Review carousel state
  const [currentReview, setCurrentReview] = useState(reviews.length);
  const [visibleSlides, setVisibleSlides] = useState(1);
  
  // Popup state
  const [showPromoPopup, setShowPromoPopup] = useState(false);

  // Window resize handler for responsive slides
  useEffect(() => {
    const handleResize = () => {
      // Add a small debounce to avoid too many updates
      const timeoutId = setTimeout(() => {
        if (window.innerWidth >= 1024) {
          setVisibleSlides(4); // Desktop
        } else if (window.innerWidth >= 640) {
          setVisibleSlides(2); // Tablet
        } else {
          setVisibleSlides(1); // Mobile
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    };

    // Set initial value
    handleResize();

    // Add event listener with debounce
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Image slideshow effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 5000);
    
    return () => clearInterval(timer);
  }, []);

  // Review carousel reset effect
  useEffect(() => {
    if (currentReview === reviews.length * 2) {
      const timer = setTimeout(() => {
        setCurrentReview(reviews.length);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentReview, reviews.length]);

  // Review carousel auto-rotation effect with pause on hover
  useEffect(() => {
    const reviewTimer = setInterval(() => {
      setCurrentReview((prev) => {
        const nextSlide = prev + 1;
        if (nextSlide === reviews.length * 2) {
          setTimeout(() => {
            setCurrentReview(reviews.length);
          }, 0);
          return nextSlide;
        }
        return nextSlide;
      });
    }, 3000);
    
    return () => clearInterval(reviewTimer);
  }, [reviews.length]);

  // Popup trigger effect - show after 3 seconds
  useEffect(() => {
    // Check if user has already seen the popup (using localStorage)
    const hasSeenPromo = localStorage.getItem('curlyCutPromoSeen');
    
    if (!hasSeenPromo) {
      const timer = setTimeout(() => {
        setShowPromoPopup(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const closePromoPopup = () => {
    setShowPromoPopup(false);
    // Mark as seen so it doesn't show again
    localStorage.setItem('curlyCutPromoSeen', 'true');
  };



  return (
    <main className="min-h-screen bg-gradient-to-br from-[#F9F0F3] via-[#F7E8EF] to-[#F5E1EC]">
      {/* Decorative background elements */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#FFF5F8] rounded-full mix-blend-multiply filter blur-3xl opacity-50" />
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-[#FCE9F1] rounded-full mix-blend-multiply filter blur-3xl opacity-50" />
      </div>
      
    {/* Hero Section */}
    <section className="container mx-auto px-4 sm:px-6 pt-16 sm:pt-20 lg:pt-28">
    <div className="flex flex-col lg:flex-row justify-between items-center lg:items-start relative min-h-[500px] lg:min-h-[700px]">
        {/* Left Content */}
        <div className="max-w-full lg:max-w-[600px] z-10 pt-8 lg:pt-24 text-center lg:text-left">
        {/* SALON NAME */}
        <h2 className="font-playfair font-bold text-[52px] sm:text-[72px] md:text-[92px] leading-[1.1] tracking-tight mb-6 lg:mb-8 mt-8 lg:mt-20 text-black/90">
            <span className="inline-block mr-1">Cut</span>
            <span className="relative inline-block mx-1">
            <span className="relative z-10 italic">N</span>
            <span className="absolute inset-0 bg-gradient-to-r from-[#FFB6C1]/40 to-[#FFB6C1]/20 blur-xl -z-10 scale-150"/>
            </span>
            <span className="inline-block ml-1">Curls</span>
        </h2>

        {/* TAGLINE */}
        <div className="relative mb-10 lg:mb-14">
            <p className="font-grotesk font-light text-[20px] sm:text-[24px] lg:text-[30px] tracking-wide text-gray-800/90">
            Where{" "}
            <span className="relative inline-block">
                <span className="relative z-10 text-[#FFB6C1] font-normal">style</span>
                <span className="absolute inset-0 bg-gradient-to-r from-[#FFB6C1]/20 to-transparent blur-md -z-10"/>
            </span>{" "}
            and{" "}
            <span className="relative inline-block">
                <span className="relative z-10 text-[#FFB6C1] font-normal">passion</span>
                <span className="absolute inset-0 bg-gradient-to-r from-[#FFB6C1]/20 to-transparent blur-md -z-10"/>
            </span>{" "}
            are born
            </p>
            <div className="absolute left-1/2 lg:left-0 -bottom-6 w-32 h-[1px] bg-gradient-to-r from-[#FFB6C1]/50 to-transparent transform -translate-x-1/2 lg:translate-x-0"/>
        </div>

        {/* BUTTON */}
        <div className="flex items-center justify-center lg:justify-start gap-8">
            <a
            href="https://square.site/book/5CPJFK4WYVQF3/cut-n-curls-salon-manhasset-ny"
            className="bg-black/90 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full hover:bg-black transition-colors font-light tracking-wide backdrop-blur-sm"
            >
            Book Now
            </a>
        </div>
        </div>

        {/* Right Content with Dome and Image */}
        <div className="relative w-full max-w-[500px] lg:max-w-[750px] h-[500px] lg:h-[700px] mt-12 lg:mt-0">
        {/* Dome background effect */}
        <div
            className="absolute right-0 top-0 w-full lg:w-[600px] h-full rounded-t-full"
            style={{
            background: "linear-gradient(135deg, #EED8E4 0%, #EAD2E0 100%)",
            boxShadow: "inset 0 0 50px rgba(0, 0, 0, 0.15)",
            }}
        />
        
        {/* Model Image */}
        <div className="absolute right-0 top-0 w-full lg:w-[600px] h-full">
            <div className="absolute inset-0 bg-gradient-to-t from-[#F5E1EC]/50 to-transparent mix-blend-overlay" />
            <img
            src="/images/home/model.png"
            alt="Beauty model"
            className="w-full h-full object-cover object-center"
            />
        </div>

        {/* Decorative elements - Hidden on mobile, visible on larger screens */}
        <div className="hidden lg:block absolute -left-12 top-1/4 w-24 h-24 rounded-full bg-white/10 backdrop-blur-sm" />
        <div className="hidden lg:block absolute -right-16 bottom-1/3 w-32 h-32 rounded-full bg-white/20 backdrop-blur-sm" />
        </div>

        {/* Background decoration */}
        <div className="absolute top-0 left-0 -z-10 w-full h-full overflow-hidden">
        <div className="absolute top-[20%] left-[10%] w-[200px] lg:w-[300px] h-[200px] lg:h-[300px] bg-[#FFB6C1]/5 rounded-full mix-blend-multiply filter blur-3xl" />
        <div className="absolute bottom-[30%] right-[20%] w-[150px] lg:w-[250px] h-[150px] lg:h-[250px] bg-[#FFE4E1]/5 rounded-full mix-blend-multiply filter blur-2xl" />
        </div>
    </div>
    </section>

      {/* Services Section */}
        <section className="bg-gradient-to-b from-[#A47C89] via-[#916573] to-[#7E545F] text-white mt-0 py-16 sm:py-24 lg:py-32 relative overflow-hidden">
        {/* Background Elements for softer transition */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-[#F5E1EC] via-[#F5E1EC]/50 to-transparent" />
            <div className="absolute top-1/3 right-1/3 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-[#B08B59]/20 rounded-full mix-blend-overlay filter blur-[80px] lg:blur-[120px]" />
            <div className="absolute bottom-1/3 left-1/3 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-[#B08B59]/15 rounded-full mix-blend-overlay filter blur-[80px] lg:blur-[120px]" />
        </div>

        {/* Header */}
        <div className="container mx-auto px-4 sm:px-6 text-center mb-16 lg:mb-24">
            <span className="text-sm sm:text-base uppercase tracking-[0.4em] text-[#DAB17A] font-bold drop-shadow-md">Experience luxury</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl mt-3 mb-4 sm:mb-6 text-white font-semibold tracking-wide">Our Premier Services</h2>
            <p className="text-base sm:text-lg text-white/90 max-w-3xl mx-auto leading-relaxed font-medium px-4">
            Join us at our individually designed salon to experience our globally recognized styling services and complimentary beauty treatments.
            </p>
        </div>

        {/* Services Grid */}
        <div className="container mx-auto px-4 sm:px-6">
            <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-32">
            {/* Left Column - Two Images Stack */}
            <div className="space-y-16 sm:space-y-24 lg:space-y-32">
                {/* Top Image */}
                <div className="relative">
                <div className="w-full max-w-[530px] h-[300px] sm:h-[360px] relative mx-auto">
                    <img
                    src="/images/home/color.jpg"
                    alt="Color Services"
                    className="w-full h-full object-cover"
                    />
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 w-[90%] sm:w-[480px] h-full border-2 border-[#B08B59] pointer-events-none" />
                </div>
                <div className="mt-8 sm:mt-10 pl-4 sm:pl-8 relative">
                    <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#B08B59] to-[#B08B59]/50" />
                    <h5 className="text-sm sm:text-base uppercase tracking-[0.2em] text-[#DAB17A] font-bold drop-shadow-md">Our speciality</h5>
                    <h3 className="text-2xl sm:text-3xl mb-3 sm:mb-4 text-white font-semibold tracking-wide">Color & Treatment</h3>
                    <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-xl font-medium">
                    Our colorists work one-on-one with you to create personalized color that complements your style and features.
                    From single process to balayage, we offer comprehensive color services with optional glazing and treatments.
                    </p>
                </div>
                </div>

                {/* Bottom Image */}
                <div className="relative">
                <div className="w-full max-w-[530px] h-[350px] sm:h-[450px] relative mx-auto">
                    <img
                    src="/images/home/styling.jpg"
                    alt="Styling Services"
                    className="w-full h-full object-cover"
                    />
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 w-[90%] sm:w-[480px] h-full border-2 border-[#B08B59] pointer-events-none" />
                </div>
                <div className="mt-8 sm:mt-10 pl-4 sm:pl-8 relative">
                    <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#B08B59] to-[#B08B59]/50" />
                    <h5 className="text-sm sm:text-base uppercase tracking-[0.2em] text-[#DAB17A] font-bold drop-shadow-md">Expert care</h5>
                    <h3 className="text-2xl sm:text-3xl mb-3 sm:mb-4 text-white font-semibold tracking-wide">Styling & Cuts</h3>
                    <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-xl font-medium">
                    Enjoy a luxurious shampoo and professional styling. Our expert stylists customize each cut to fit your lifestyle
                    and personal style, using advanced techniques for the perfect finish.
                    </p>
                </div>
                </div>
            </div>

            {/* Right Column - Single Tall Image */}
            <div className="relative mt-16 sm:mt-24 lg:mt-48">
                <div className="w-full max-w-[420px] h-[400px] sm:h-[540px] relative mx-auto lg:mx-0 lg:-ml-0">
                <img
                    src="/images/home/consult.jpg"
                    alt="Specialty Services"
                    className="w-full h-full object-cover"
                />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 w-[90%] sm:w-[380px] h-full border-2 border-[#B08B59] pointer-events-none" />
                </div>
                <div className="mt-8 sm:mt-10 pl-4 sm:pl-8 relative">
                <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#B08B59] to-[#B08B59]/50" />
                <h5 className="text-sm sm:text-base uppercase tracking-[0.2em] text-[#DAB17A] font-bold drop-shadow-md">Unique treatments</h5>
                <h3 className="text-2xl sm:text-3xl mb-3 sm:mb-4 text-white font-semibold tracking-wide">Specialty Services</h3>
                <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-xl font-medium">
                    Complete your look with our specialized treatments and services. From keratin treatments to threading services,
                    we offer comprehensive beauty solutions.
                </p>
                </div>
            </div>
            </div>
        </div>
        </section>

    {/* About Section */}
    <section className="bg-gradient-to-br from-[#F9F0F3] via-[#F7E8EF] to-[#F5E1EC] py-20 sm:py-32 lg:py-40 relative overflow-hidden">
    {/* Background Elements */}
    <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[300px] sm:w-[400px] lg:w-[500px] h-[300px] sm:h-[400px] lg:h-[500px] bg-[#FFF5F8] rounded-full mix-blend-multiply filter blur-3xl opacity-50" />
        <div className="absolute bottom-1/4 right-1/4 w-[350px] sm:w-[450px] lg:w-[600px] h-[350px] sm:h-[450px] lg:h-[600px] bg-[#FCE9F1] rounded-full mix-blend-multiply filter blur-3xl opacity-50" />
    </div>

    <div className="container mx-auto px-4 sm:px-6">
        <div className="relative min-h-[600px] lg:min-h-[800px]">
        {/* Text Box */}
        <div className="bg-white/90 backdrop-blur-sm w-full lg:w-[60%] rounded-[10px] p-6 sm:p-10 lg:p-16 shadow-[0_20px_50px_rgba(255,182,193,0.15)] relative z-10 lg:translate-y-[10%] transition-transform">
            <div className="max-w-full lg:max-w-[60%]">
            <div className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 bg-[#FFB6C1]/10 rounded-full mb-6 sm:mb-8">
                <p className="text-[#FFB6C1] text-xs sm:text-sm font-medium tracking-wide">OUR MISSION</p>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-[52px] leading-[1.1] font-medium mb-6 sm:mb-8 tracking-tight">
                Elevating beauty 
                <br/>
                through{" "}
                <span className="relative">
                <span className="relative z-10 text-[#FFB6C1]">excellence</span>
                <span className="absolute inset-0 bg-gradient-to-r from-[#FFB6C1]/20 to-[#FFB6C1]/5 blur-xl -z-10 transform -rotate-2" />
                </span>
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                Cut N Curls pledge to always provide our customers with the best hair services 
                which will boost their confidence, accentuate their beauty, and give them a 
                remarkable appearance.
            </p>
            </div>
        </div>

        {/* Video Container - Stack on mobile, absolute on desktop */}
        <div className="relative lg:absolute lg:top-0 lg:right-[5%] w-full lg:w-[40%] z-20 mt-6 lg:mt-0">
            <div className="aspect-video relative rounded-[10px] overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.15)] transform hover:scale-[1.01] transition-transform duration-500">
            <video
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                src="/videos/home/salon.mp4"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent"></div>
            </div>
        </div>

        {/* Slideshow - Stack on mobile, absolute on desktop */}
        <div className="relative lg:absolute lg:bottom-0 lg:right-[15%] w-full lg:w-[50%] z-30 mt-6 lg:mt-0">
            <div className="aspect-[16/9] relative rounded-[10px] overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.15)] transform hover:scale-[1.01] transition-transform duration-500">
            {/* Slideshow images */}
            <div className={`absolute inset-0 transition-opacity duration-1000 ${
                currentSlide === 0 ? 'opacity-100' : 'opacity-0'
            }`}>
                <img
                src="/images/home/image1.jpg"
                alt="Salon showcase 1"
                className="w-full h-full object-cover object-center transform scale-105"
                />
            </div>
            <div className={`absolute inset-0 transition-opacity duration-1000 ${
                currentSlide === 1 ? 'opacity-100' : 'opacity-0'
            }`}>
                <img
                src="/images/home/image2.jpg"
                alt="Salon showcase 2"
                className="w-full h-full object-cover object-center transform scale-105"
                />
            </div>
            <div className={`absolute inset-0 transition-opacity duration-1000 ${
                currentSlide === 2 ? 'opacity-100' : 'opacity-0'
            }`}>
                <img
                src="/images/home/image3.jpg"
                alt="Salon showcase 3"
                className="w-full h-full object-cover object-center transform scale-105"
                />
            </div>

            {/* Overlay fade */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            
            {/* Slide Indicators */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
                {[0, 1, 2].map((index) => (
                <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-500 ${
                    currentSlide === index 
                    ? 'w-8 bg-white' 
                    : 'bg-white/60 hover:bg-white/80'
                    }`}
                />
                ))}
            </div>
            </div>
        </div>
        </div>
    </div>
    </section>

    {/* Reviews Section */}
    <section className="py-20 sm:py-32 lg:py-40 bg-gradient-to-b from-[#916573] to-[#7E545F] relative overflow-hidden">
      {/* Background Overlays */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/3 right-1/3 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-[#B08B59]/15 rounded-full blur-[80px] lg:blur-[120px] opacity-50" />
        <div className="absolute bottom-1/3 left-1/3 w-[300px] sm:w-[400px] lg:w-[600px] h-[300px] sm:h-[400px] lg:h-[600px] bg-[#B08B59]/10 rounded-full blur-[80px] lg:blur-[120px] opacity-40" />
      </div>

      <div className="container mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16 lg:mb-24">
          <h2 className="text-sm sm:text-base uppercase tracking-[0.2em] text-white font-bold mb-2">
            What Our Clients Say
          </h2>
          <div className="flex flex-col items-center gap-4">
            <img 
              src="/images/home/google.png" 
              alt="Google Reviews" 
              className="h-10 sm:h-14 mt-2"
            />
            <div className="flex items-center justify-center gap-1.5 sm:gap-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 sm:w-6 sm:h-6 fill-[#DAB17A] text-[#DAB17A]" />
              ))}
            </div>
            <p className="text-sm sm:text-base font-medium text-[#DAB17A] tracking-wide">
              Based on over 200+ reviews
            </p>
          </div>
        </div>

        {/* Reviews Carousel */}
        <div className="relative max-w-[1400px] mx-auto">
          <div className="overflow-hidden px-4 sm:px-8">
            <div 
              className="flex gap-4 sm:gap-8 transition-transform duration-500 ease-in-out"
              style={{ 
                transform: `translateX(-${currentReview * (100 / visibleSlides)}%)`,
              }}
            >
              {[...reviews, ...reviews, ...reviews].map((review, index) => (
                <div
                  key={`${review.id}-${index}`}
                  className={`
                    flex-none
                    w-full
                    sm:w-[calc(50%-16px)]
                    lg:w-[calc(25%-24px)]
                    bg-white/10
                    rounded-xl sm:rounded-2xl
                    p-6 sm:p-8
                    border border-white/10
                    shadow-lg
                    transition-all duration-500
                    group hover:border-white/20
                  `}
                >
                  <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                    {review.avatar ? (
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#DAB17A] to-[#B08B59] flex items-center justify-center text-white font-medium shadow-md">
                        {review.avatar}
                      </div>
                    ) : (
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#916573] to-[#7E545F] border border-white/20 flex items-center justify-center shadow-md group-hover:border-white/30 transition-colors">
                        <span className="text-[#DAB17A]/90 text-base sm:text-lg font-light">
                          {review.name[0]}
                        </span>
                      </div>
                    )}
                    <div>
                      <h4 className="font-medium text-white group-hover:text-[#DAB17A] transition-colors duration-300">
                        {review.name}
                      </h4>
                      <p className="text-xs sm:text-sm text-white/70">
                        {review.date}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 mb-3 sm:mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#DAB17A] text-[#DAB17A]" />
                    ))}
                  </div>
                  <p className="text-sm sm:text-base text-white/90 leading-relaxed font-light line-clamp-4">
                    {review.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={() => {
              setCurrentReview((prev) => {
                if (prev === 0) {
                  return reviews.length;
                }
                return prev - 1;
              });
            }}
            className="
              absolute
              -left-2 sm:-left-3
              top-1/2
              -translate-y-1/2
              w-10 h-10 sm:w-12 sm:h-12
              rounded-full
              bg-gradient-to-br
              from-[#916573] to-[#7E545F]
              border border-white/10
              shadow-md
              flex items-center justify-center
              hover:border-white/30
              transition-all duration-300
              group z-10
            "
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-white/60 group-hover:text-white transition-colors" />
          </button>
          <button
            onClick={() => {
              setCurrentReview((prev) => {
                const nextSlide = prev + 1;
                if (nextSlide === reviews.length * 2) {
                  setTimeout(() => {
                    setCurrentReview(reviews.length);
                  }, 0);
                  return nextSlide;
                }
                return nextSlide;
              });
            }}
            className="
              absolute
              -right-2 sm:-right-3
              top-1/2
              -translate-y-1/2
              w-10 h-10 sm:w-12 sm:h-12
              rounded-full
              bg-gradient-to-br
              from-[#916573] to-[#7E545F]
              border border-white/10
              shadow-md
              flex items-center justify-center
              hover:border-white/30
              transition-all duration-300
              group z-10
            "
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-white/60 group-hover:text-white transition-colors" />
          </button>
        </div>
      </div>
    </section>

    {/* Curly Cut Promotion Popup */}
    {showPromoPopup && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
          onClick={closePromoPopup}
        />
        
        {/* Popup Content */}
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100 animate-in fade-in zoom-in">
          {/* Close Button */}
          <button
            onClick={closePromoPopup}
            className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>

          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-full h-full rounded-2xl overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#A47C89]/20 to-[#F9F0F3]/30 rounded-full -translate-y-8 translate-x-8" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-[#F9F0F3]/40 to-[#A47C89]/20 rounded-full translate-y-6 -translate-x-6" />
          </div>

          {/* Content */}
          <div className="relative p-8 text-center">

            {/* Badge */}
            <div className="inline-block bg-gradient-to-r from-[#A47C89] to-[#916573] text-white text-xs uppercase tracking-wider px-3 py-1 rounded-full mb-4">
              Limited Time Offer
            </div>

            {/* Headline */}
            <h2 className="text-2xl font-playfair mb-3 text-gray-800">
              Introducing the
              <span className="block text-[#A47C89] italic">Dry Curly Cut</span>
            </h2>

            {/* Certification Badge */}
            <div className="bg-[#F9F0F3] rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium text-[#A47C89]">Certified by Cado Academy</span><br />
                One of the few officially certified salons on Long Island
              </p>
            </div>

            {/* Offer Details */}
            <div className="mb-6">
              <p className="text-gray-700 text-sm leading-relaxed mb-3">
                First-time curly cut clients get a
              </p>
              <div className="bg-gradient-to-r from-[#A47C89] to-[#916573] text-white px-4 py-2 rounded-lg inline-block mb-3">
                <span className="font-semibold">FREE 4-Week Hair Treatment Kit</span>
              </div>
              <p className="text-xs text-gray-500">
                Keep your curls healthy, fresh, and fabulous • Valid until August 31, 2025
              </p>
            </div>

            {/* CTA Button */}
            <a
              href="https://book.squareup.com/appointments/thd0mtsw6yo1sc/location/5CPJFK4WYVQF3/services/MNXVWBOHDH3AQJMPSB5DJDEW"
              onClick={closePromoPopup}
              className="w-full bg-gradient-to-r from-[#A47C89] to-[#916573] text-white py-3 px-6 rounded-lg font-medium hover:from-[#916573] hover:to-[#7D5A66] transition-all duration-300 transform hover:scale-105 shadow-lg inline-block"
            >
              Book Your Curly Cut Today
            </a>

            {/* Fine Print */}
            <p className="text-xs text-gray-400 mt-4">
              New clients only • Cannot be combined with other offers
            </p>
          </div>
        </div>
      </div>
    )}
    </main>
  )
}